import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import PermissionDeniedCard from '../components/PermissionDeniedCard';
import usePermissions from '../hooks/usePermissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const ACCESS_LOG_PERMISSION = 'ADMIN_ULTIMI_ACCESSI';
const ACCESS_LOG_DAYS = 60;

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

export default function AdminRecentAccessesPage() {
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedUser, setSelectedUser] = useState('all');

  const canView = hasPermission(ACCESS_LOG_PERMISSION);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/admin/access-logins/recent');
        setItems(response.data?.items || []);
      } catch (error) {
        console.error('Failed to fetch recent access logs', error);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    if (permissionsLoading) {
      return;
    }

    if (!canView) {
      setLoading(false);
      return;
    }

    fetchData();
  }, [permissionsLoading, canView]);

  const userOptions = useMemo(() => {
    const uniqueNames = new Set();
    (items || []).forEach((row) => {
      const name = (row?.full_name || '').trim() || 'Utente sconosciuto';
      uniqueNames.add(name);
    });
    return Array.from(uniqueNames)
      .map((name) => ({ value: name, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label, 'it', { sensitivity: 'base' }));
  }, [items]);

  const rows = useMemo(() => {
    const parsedFrom = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    const parsedTo = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;

    return (items || []).filter((row) => {
      const rowName = (row?.full_name || '').trim() || 'Utente sconosciuto';
      const rowDate = row?.login_at ? new Date(row.login_at) : null;

      if (selectedUser !== 'all' && rowName !== selectedUser) {
        return false;
      }

      if (parsedFrom && rowDate && rowDate < parsedFrom) {
        return false;
      }

      if (parsedTo && rowDate && rowDate > parsedTo) {
        return false;
      }

      return true;
    });
  }, [items, selectedUser, dateFrom, dateTo]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="mx-auto max-w-7xl px-4 pb-6 pt-20 sm:px-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold sm:text-3xl">Lista ultimi accessi</h1>
          <p className="text-gray-600">
            Accessi degli utenti degli ultimi {ACCESS_LOG_DAYS} giorni (più recenti in alto)
          </p>
        </div>

        {permissionsLoading || loading ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-600">
              Caricamento...
            </CardContent>
          </Card>
        ) : !canView ? (
          <div className="flex justify-center">
            <PermissionDeniedCard message="Permesso ADMIN_ULTIMI_ACCESSI richiesto" />
          </div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Storico accessi</CardTitle>
              <CardDescription>Nome utente, IP, data e ora del login</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-4">
                <div>
                  <Label htmlFor="access-filter-from">Da</Label>
                  <Input
                    id="access-filter-from"
                    type="date"
                    value={dateFrom}
                    max={dateTo || undefined}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="access-filter-to">A</Label>
                  <Input
                    id="access-filter-to"
                    type="date"
                    value={dateTo}
                    min={dateFrom || undefined}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="access-filter-user">Utente</Label>
                  <Select value={selectedUser} onValueChange={setSelectedUser}>
                    <SelectTrigger id="access-filter-user">
                      <SelectValue placeholder="Tutti gli utenti" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti gli utenti</SelectItem>
                      {userOptions.map((userOption) => (
                        <SelectItem key={userOption.value} value={userOption.value}>
                          {userOption.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setDateFrom('');
                      setDateTo('');
                      setSelectedUser('all');
                    }}
                  >
                    Reset filtri
                  </Button>
                </div>
              </div>

              <p className="mb-4 text-sm text-gray-600">
                Mostrati {rows.length} accessi su {items.length}
              </p>

              {rows.length === 0 ? (
                <p className="py-6 text-center text-sm text-gray-500">Nessun accesso registrato negli ultimi 60 giorni</p>
              ) : (
                <div className="space-y-3">
                  <div className="space-y-3 sm:hidden">
                    {rows.map((row, idx) => (
                      <div key={`${row.login_at || 'n/a'}-${idx}`} className="rounded-lg border p-3">
                        <p className="font-medium">{row.full_name || 'Utente sconosciuto'}</p>
                        <p className="text-sm text-gray-700 mt-1">
                          <span className="font-medium">Data e ora:</span> {formatDateTime(row.login_at)}
                        </p>
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">IP:</span> {row.ip_address || '-'}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="hidden overflow-x-auto sm:block">
                    <table className="w-full min-w-[760px] text-sm">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="py-2 pr-3 font-medium">Nome</th>
                          <th className="py-2 pr-3 font-medium">IP</th>
                          <th className="py-2 pr-3 font-medium">Data e ora</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, idx) => (
                          <tr key={`${row.login_at || 'n/a'}-${idx}`} className="border-b last:border-b-0">
                            <td className="py-3 pr-3 font-medium">{row.full_name || 'Utente sconosciuto'}</td>
                            <td className="py-3 pr-3">{row.ip_address || '-'}</td>
                            <td className="py-3 pr-3">{formatDateTime(row.login_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
